import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BillingDataService } from '@core/data/api/billing/billing-data.service';
import { Billing } from '@core/models/billing/billing';
import { BillingAlert } from '@core/models/billing/billing-alert';
import { BillingInvoice } from '@core/models/billing/billing-invoice';
import { BillingNotice } from '@core/models/billing/billing-notice';
import { BillingNoticeKey } from '@core/models/billing/billing-notice-key';
import { BillingSubscription } from '@core/models/billing/billing-subscription';
import { SubscriptionModel } from '@core/models/billing/subscription-model';
import { SubscriptionPlan } from '@core/models/billing/subscription-plan';
import { SubscriptionTier } from '@core/models/billing/subscription-tier';
import { SubscriptionType } from '@core/models/billing/subscription-type';
import { CreateBillingAction, FetchBillingAction, FetchKeyAction, RequestUpgradeAction, StartBillingDetailsAction, StartPaymentMethodAction, StartSubscribeAction, SubscribeAction, UpdateBillingAction } from '@core/state/billing/billing-actions';
import {
    getBIllingInvoices,
    getBilling,
    getSubscription,
    getSubscriptions,
    isBillingFetching,
    isBillingInvoicesFetching,
    isBillingInvoicesFetchingMore,
    isBillingKeyFetching
} from '@core/state/billing/billing-selectors';
import { IBillingState } from '@core/state/billing/billing-state';
import { IObservableAction } from '@core/state/shared/actions/observable-action';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, map } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class BillingService {
    constructor(
        private billingDataService: BillingDataService,
        private store$: Store<IBillingState>
    ) {}

    fetchBilling() {
        this.store$.dispatch(new FetchBillingAction());
    }

    getBilling(): Observable<any> {
        return this.store$.select(getBilling);
    }

    isBillingFetching(): Observable<boolean> {
        return this.store$.select(isBillingFetching);
    }

    createBilling(billingDetails: any): IObservableAction {
        let action: any = new CreateBillingAction(billingDetails);
        this.store$.dispatch(action);
        return action;
    }

    updateBilling(billingDetails: any): IObservableAction {
        let action: any = new UpdateBillingAction(billingDetails);
        this.store$.dispatch(action);
        return action;
    }
    
    hasBillingDetails(): Observable<boolean> {
        return this.store$.select(getBilling).pipe(
            map(
                (billing: Billing) =>
                    billing &&
                    billing.payment_sources &&
                    billing.payment_sources.length
            ),
            map((val) => !!val)
        );
    }

    getSubscription(
        subscriptionType?: SubscriptionType
    ): Observable<BillingSubscription> {
        if (!subscriptionType) {
            return this.store$.select(getSubscription);
        } else {
            return this.store$.select(getSubscriptions).pipe(
                map((subscriptions) => {
                    return (
                        subscriptions &&
                        subscriptions.find(
                            (subscription: BillingSubscription) =>
                                subscription.metadata.type === subscriptionType
                        )
                    );
                })
            );
        }
    }

    isIHDSubscription(): Observable<boolean> {
        return this.getSubscription(SubscriptionType.IHD).pipe(
            map((subscription) => Boolean(subscription))
        );
    }

    hasOnlyIHDSubscription(): Observable<boolean> {
        return combineLatest([
            this.getSubscription(),
            this.getSubscription(SubscriptionType.IHD),
        ]).pipe(
            map(([subscription, IHDSubscription]) => {
                let hasOnlyIHDSubscription: boolean = false;

                // Detect if we are an IHD customer only
                if (
                    subscription &&
                    IHDSubscription &&
                    subscription.price === 0 &&
                    subscription.metadata?.model ===
                        SubscriptionModel.PER_LOCATION
                ) {
                    hasOnlyIHDSubscription = true;
                }

                return hasOnlyIHDSubscription;
            })
        );
    }

    getBillingAlerts(): Observable<BillingAlert[]> {
        return combineLatest([
            this.getBilling(),
            this.hasOnlyIHDSubscription()
        ]).pipe(
            map(([billing, hasOnlyIHDSubscription]: [any, boolean]) => {
                let alerts: BillingAlert[] = [];

                if (billing && billing.notices && billing.notices.length) {
                    alerts = billing.notices.map((notice) => {
                        const billingNotice: BillingNotice = Object.assign(
                            new BillingNotice(),
                            notice
                        );

                        return {
                            action: billingNotice.alertAction,
                            key: billingNotice.key,
                            style: billingNotice.alertStyle,
                            title: billingNotice.alertTitle(
                                hasOnlyIHDSubscription
                            ),
                            description: billingNotice.alertDescription(
                                hasOnlyIHDSubscription
                            ),
                            badge: billingNotice.alertBadge,
                            data: billingNotice.data,
                            type: billingNotice.billingActionType,
                            severity: billingNotice.severity
                        };
                    });
                }

                return alerts;
            })
        );
    }

    getNavBarAlert(): Observable<BillingAlert> {
        return this.getBillingAlerts().pipe(
            map((alerts: BillingAlert[]) => {
                let alert: BillingAlert = null;

                if (alerts && alerts.length) {
                    alert = alerts.find(
                        (billingAlert: BillingAlert) =>
                            billingAlert.key ===
                                BillingNoticeKey.CARD_EXPIRED ||
                            billingAlert.key ===
                                BillingNoticeKey.CARD_NEAR_EXPIRY ||
                            billingAlert.key ===
                                BillingNoticeKey.PAYMENT_FAILURE ||
                            billingAlert.key === BillingNoticeKey.PAST_DUE ||
                            billingAlert.key === BillingNoticeKey.UNPAID
                    );

                    if (!alert) {
                        alert = alerts.find(
                            (billingAlert: BillingAlert) =>
                                billingAlert.key ===
                                BillingNoticeKey.CARD_NEAR_EXPIRY
                        );
                    }
                }

                return alert;
            })
        );
    }

    startSubscription(
        planId: SubscriptionPlan,
        tier: SubscriptionTier = null,
        showPlanDetails: boolean = true,
        onlyBilling: boolean = false
    ): IObservableAction {
        let action: any = new StartSubscribeAction({
            planId,
            tier,
            showPlanDetails,
            onlyBilling,
        });

        this.store$.dispatch(action);
        return action;
    }

    createSubscription(planId: SubscriptionPlan): IObservableAction {
        let action: any = new SubscribeAction({ planId });
        this.store$.dispatch(action);
        return action;
    }

    getInvoices(): Observable<BillingInvoice[]> {
        return this.store$.select(getBIllingInvoices);
    }

    isInvoicesFetching(): Observable<boolean> {
        return this.store$.select(isBillingInvoicesFetching)
    }

    isInvoicesFetchingMore(): Observable<boolean> {
        return this.store$.select(isBillingInvoicesFetchingMore)
    }

    requestUpgrade(): IObservableAction {
        let action: any = new RequestUpgradeAction();
        this.store$.dispatch(action);
        return action;
    }

    startUpdateBillingDetails() {
        this.store$.dispatch(new StartBillingDetailsAction());
    }

    startUpdatePaymentMethod() {
        this.store$.dispatch(new StartPaymentMethodAction());
    }

    fetchKey() {
        this.store$.dispatch(new FetchKeyAction());
    }

    isKeyFetching(): Observable<boolean> {
        return this.store$.select(isBillingKeyFetching);
    }

    fetchStripeKey() {
        return this.billingDataService.fetchStripeKey();
    }

    fetchPlaidKey() {
        return this.billingDataService.fetchPlaidKey();
    }
}